import React from 'react';
import { useNavigate, useLocation, Navigate, Routes, Route } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { NavLink } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import Dashboard from './Dashboard';
import Users from './Users';
import Books from './Books';
import Requests from './Requests';
import Analytics from './Analytics';
import Login from './Login';
import LandingPage from './LandingPage';
import { ROUTES } from '../config/routes';
import bifHeader from '../assets/shiriki-logo-header.png';
import './app.css';

type RoleLabel = 'SUPERADMIN' | 'ADMIN' | 'BOOKLOVER' | 'NONE';

type AuthResponse = {
  data: {
    session: Session | null;
  };
  error: Error | null;
};

function useAuthAndRole() {
  const supabase = getSupabaseClient();
  const [userEmail, setUserEmail] = React.useState<string | null>(null);
  const [role, setRole] = React.useState<RoleLabel>('NONE');
  const [loading, setLoading] = React.useState(true);

  const checkUserRole = React.useCallback(async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('role')
        .eq('email', email)
        .single();

      if (error) {
        console.error('useAuthAndRole: Failed to fetch role:', error);
        return 'NONE' as RoleLabel;
      }

      if (!data) {
        console.log('useAuthAndRole: No user record found');
        return 'NONE' as RoleLabel;
      }

      console.log('useAuthAndRole: Role fetched:', data.role);
      return (data.role === 'superadmin' ? 'SUPERADMIN' :
              data.role === 'admin' ? 'ADMIN' :
              data.role === 'booklover' ? 'BOOKLOVER' : 'NONE') as RoleLabel;
    } catch (error) {
      console.error('useAuthAndRole: Role check error:', error);
      return 'NONE' as RoleLabel;
    }
  }, [supabase]);

  React.useEffect(() => {
    let mounted = true;
    
    const handleAuthChange = async (session: any) => {
      if (!mounted) return;
      
      setLoading(true);
      
      try {
        if (!session?.user?.email) {
          console.log('useAuthAndRole: No user session');
          setUserEmail(null);
          setRole('NONE');
          return;
        }

        console.log('useAuthAndRole: User found:', session.user.email);
        setUserEmail(session.user.email);
        
        const userRole = await checkUserRole(session.user.email);
        if (mounted) {
          setRole(userRole);
        }
      } catch (error) {
        console.error('useAuthAndRole: Auth check failed:', error);
        if (mounted) {
          setUserEmail(null);
          setRole('NONE');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Initial session check
    console.log('useAuthAndRole: Checking initial session...');
    supabase.auth.getSession().then((response: AuthResponse) => {
      handleAuthChange(response.data.session);
    });

    // Set up auth state change listener
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event: string, session: any) => {
      console.log('useAuthAndRole: Auth state changed, event:', event);
      handleAuthChange(session);
    });

    return () => {
      console.log('useAuthAndRole: Cleaning up...');
      mounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, checkUserRole]);

  return { userEmail, role, loading, supabase };
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { role, loading } = useAuthAndRole();
  const location = useLocation();

  if (loading) {
    return (
      <div className="content">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (role === 'SUPERADMIN' || role === 'ADMIN') {
    return children;
  }

  return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
}

export default function App() {
  const navigate = useNavigate();
  const { userEmail, role, loading, supabase } = useAuthAndRole();

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate(ROUTES.LOGIN);
  };

  const RoleBadge = () => (
    <span
      style={{
        marginLeft: 8,
        fontSize: 12,
        padding: '3px 6px',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        background:
          role === 'SUPERADMIN' ? '#111' :
          role === 'ADMIN' ? '#007bff' :
          role === 'BOOKLOVER' ? '#28a745' : '#eee',
        color: (role === 'NONE' || role === 'BOOKLOVER') ? '#111' : '#fff',
      }}
      title="Your admin privilege"
    >
      {role}
    </span>
  );

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Show admin layout for logged-in admins
  if (userEmail && (role === 'ADMIN' || role === 'SUPERADMIN')) {
    return (
      <div className="layout">
        <header className="header">
          <div className="brand">
            <img src={bifHeader} alt="Shiriki" className="brand-logo" />
          </div>
          <nav className="nav">
            <NavLink to={ROUTES.DASHBOARD}>Dashboard</NavLink>
            <NavLink to={ROUTES.USERS}>Users</NavLink>
            <NavLink to={ROUTES.BOOKS}>Books</NavLink>
            <NavLink to={ROUTES.REQUESTS}>Requests</NavLink>
            <NavLink to={ROUTES.ANALYTICS}>Analytics</NavLink>
          </nav>
          <div className="auth">
            <span className="user">{userEmail}</span>
            <RoleBadge />
            <button onClick={signOut}>Sign out</button>
          </div>
        </header>
        <main className="content">
          <Routes>
            <Route path={ROUTES.DASHBOARD} element={<RequireAdmin><Dashboard /></RequireAdmin>} />
            <Route path={ROUTES.USERS} element={<RequireAdmin><Users /></RequireAdmin>} />
            <Route path={ROUTES.BOOKS} element={<RequireAdmin><Books /></RequireAdmin>} />
            <Route path={ROUTES.REQUESTS} element={<RequireAdmin><Requests /></RequireAdmin>} />
            <Route path={ROUTES.ANALYTICS} element={<RequireAdmin><Analytics /></RequireAdmin>} />
            <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
          </Routes>
        </main>
        <footer className="footer">
          <div className="footer-content">
            © {new Date().getFullYear()} Shiriki Admin
          </div>
        </footer>
      </div>
    );
  }

  // Show landing page or login page for non-admin users
  return (
    <Routes>
      <Route path={ROUTES.LANDING} element={<LandingPage />} />
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
    </Routes>
  );
}